import {
  MODE_APPOINTMENT,
  MODE_CONTACT,
  QUESTION_NAMES,
} from 'client-portal/utils/prescreener/constants';
import { bannerError } from 'ember-simplepractice/utils/error-handling';
import { cached, tracked } from '@glimmer/tracking';
import { didCancel } from 'ember-concurrency';
import { waitForRender } from 'ember-simplepractice/utils/waiters';
import ENV from 'client-portal/config/environment';
import Service, { inject as service } from '@ember/service';
import moment from 'moment-timezone';

const DEFAULT_RESULT_LIMIT = ENV.maxPageSize;

export default class PrescreenerIndexService extends Service {
  @service('prescreener/validation') validation;
  @service('prescreener/global-monarch-channel') globalMonarchChannelService;
  @service('prescreener/preview') preview;
  @service('prescreener/step') step;
  @service('prescreener/upload') upload;
  @service currentPractice;
  @service intl;
  @service router;
  @service session;
  @service store;

  @tracked questionRecord;
  @tracked disabledByChannelMismatch = null;

  get globalMonarchChannel() {
    return this.globalMonarchChannelService.channel;
  }

  set globalMonarchChannel(value) {
    this.globalMonarchChannelService.channel = value;
  }

  answerRecord;
  showPrescreenerWithClientAccess;

  get currentGlobalMonarchChannelId() {
    return this.globalMonarchChannelService.currentChannelId;
  }

  get hasNavCategories() {
    return !!this.preview.previewConfig?.categories?.length || !!this.step.categories?.length;
  }

  get questions() {
    return this.questionRecord?.questions;
  }

  get isContactFormEnabled() {
    return this.featurePrescreenerV2 && this.#shouldShowFor(MODE_CONTACT);
  }

  get isAppointmentFormEnabled() {
    return this.featurePrescreenerV2 && this.#shouldShowFor(MODE_APPOINTMENT);
  }

  @cached
  get featurePrescreenerV2() {
    return (
      (this.#isPrescreenerV2Enabled() && !this.#isDisabledByChannelMismatch) ||
      this.preview.isPreview
    );
  }

  get #isDisabledByChannelMismatch() {
    return (
      Boolean(this.session.channel) &&
      this.#getBaseChannelName(this.disabledByChannelMismatch) ===
        this.#getBaseChannelName(this.session.channel)
    );
  }

  #getBaseChannelName(channelName) {
    if (!channelName) return null;
    return channelName.split('_')[0];
  }

  #isPrescreenerV2Enabled() {
    return (
      this.#hasPrescreenerV2Feature() &&
      this.#isClientPortalMonarchOrSpWebsite() &&
      this.#canShowPrescreenerToClient()
    );
  }

  #hasPrescreenerV2Feature() {
    return this.currentPractice.get('featurePrescreenerV2');
  }

  #isClientPortalMonarchOrSpWebsite() {
    return (
      this.session.isClientPortalMonarchOrSpWebsite ||
      this.session.isWidgetChannel ||
      this.#isGlobalMonarchChannelWithPrescreenerQuestions()
    );
  }

  #isGlobalMonarchChannelWithPrescreenerQuestions() {
    return this.globalMonarchChannelService.hasPrescreenerQuestions;
  }

  #canShowPrescreenerToClient() {
    return !this.session.currentClient || this.showPrescreenerWithClientAccess;
  }

  async loadGlobalMonarchChannel() {
    return await this.globalMonarchChannelService.load();
  }

  setPreviewConfig(value) {
    this.preview.previewConfig = value;
  }

  async getContactFormAnswerData() {
    this.refreshAnswerRecord();
    return await this.upload.getEncodedAnswers(this.answerRecord);
  }

  prescreenerV2SkipValidation(client, relatedClient) {
    client?.set('skipPrescreenerV2Validation', true);
    relatedClient?.set('skipPrescreenerV2Validation', true);
  }

  reset() {
    this.questionRecord = null;
    this.disabledByChannelMismatch = null;
    this.validation.clearValidationModels();
  }

  updateAndValidateAnswer(questionData, answer, clientType = '') {
    this.validation.updateAnswerModel(questionData, answer, clientType);
    this.refreshAnswerRecord();
  }

  refreshAnswerRecord() {
    if (!this.featurePrescreenerV2) return;

    this.validation.removeUnacceptableClientTypes();

    this.answerRecord.answers = this.validation.answersForAnswerModel || [];
    this.answerRecord.files = this.upload.filesToUpload;
  }

  processAnswersForClient(client, question, value) {
    let clientProperty = question.name;
    if (question.name === QUESTION_NAMES.GENDER) {
      clientProperty = 'genderInfo';
    } else if (question.name === QUESTION_NAMES.PREFERRED_CLINICIAN) {
      clientProperty = 'clinician';
      value = value ? this.store.peekRecord('clinician', value) : undefined;
    } else if (question.name === QUESTION_NAMES.DATE_OF_BIRTH) {
      clientProperty = 'birthDate';
      value = value ? moment(value) : undefined;
    }
    client?.set(clientProperty, value);
  }

  async preloadClinicians() {
    try {
      await this.store.query('clinician', {
        fields: {
          clinician: 'firstName,lastName,middleName,suffix',
        },
        filter: { includeAll: true },
        page: {
          size: DEFAULT_RESULT_LIMIT,
        },
      });
    } catch (e) {
      // no op if preload fails
    }
  }

  async updatePrescreenerModels() {
    if (!this.featurePrescreenerV2) return;

    try {
      await this.#updateGlobalMonarchChannel();
      await this.#getQuestionAndAnswerModels();
    } catch (e) {
      this.handlePrescreenerLoadError(e);
    }
  }

  async #updateGlobalMonarchChannel() {
    if (this.globalMonarchChannel) return;

    await this.globalMonarchChannelService.load();
  }

  async #getQuestionAndAnswerModels() {
    this.questionRecord = await this.#getPrescreenerModel();

    if (this.#isDisabledForChannel()) return;

    this.answerRecord = this.#getPrescreenerAnswerModel(this.questionRecord);
    this.step.updateCategories();
  }

  async handlePrescreenerLoadError(e) {
    if (!didCancel(e)) {
      await waitForRender();
      bannerError(e, { title: this.intl.t('messages.error.prescreenerQuestionsNotLoaded') });
    }
  }

  #shouldShowFor(formType) {
    let { showOnContactForm, showOnAppointmentForm } = this.questionRecord ?? {};
    return (
      (showOnContactForm && formType === MODE_CONTACT) ||
      (showOnAppointmentForm && formType === MODE_APPOINTMENT)
    );
  }

  #isDisabledForChannel() {
    if (!this.globalMonarchChannel || this.session.isClientPortalMonarchOrSpWebsite) return false;

    if (this.#shouldDisableV2ForChannelMismatch(this.questionRecord)) {
      this.disabledByChannelMismatch = this.#getBaseChannelName(this.session.channel);
      this.questionRecord = null;
      this.answerRecord = null;
      this.validation.clearValidationModels();
      this.step.updateCategories();
      return true;
    }
  }

  #shouldDisableV2ForChannelMismatch(questionRecord) {
    let channel = this.globalMonarchChannel;
    if (!channel?.hasPrescreenerQuestions) return false;

    let currentChannelId = this.currentGlobalMonarchChannelId;
    if (!currentChannelId) return false;

    return !questionRecord?.questions.some(question => {
      return String(question.globalMonarchChannelId) === currentChannelId;
    });
  }

  async #getPrescreenerModel() {
    if (this.preview.isPreview) {
      return this.preview.getPrescreenerPreview();
    } else {
      return this.#queryStoreForPrescreenerQuestions();
    }
  }

  async #queryStoreForPrescreenerQuestions() {
    let loadedQuestions = this.store.peekAll('prescreener-question').firstObject;

    return (
      loadedQuestions ??
      (
        await this.store.query('prescreener-question', {
          'practice_id': this.currentPractice.content.id,
        })
      ).firstObject
    );
  }

  #getPrescreenerAnswerModel(prescreenerQuestion) {
    return this.store.createRecord('prescreener-answer', {
      prescreenerQuestion,
    });
  }
}
